import { BarChart3 } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { Badge, Card, CardHeader, EmptyState, mutedTextClass } from '@/components/layout/ui'
import {
  ErrorBanner,
  LoadingPane,
  Select,
  useProjectSelection,
  useWorkspace,
} from '@/features/issue'
import { NewProjectButton } from '@/features/project'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'

import { useProjectReports, type ProjectReports } from './api'
import { BucketBars, BurndownChart, FlowChart, Legend, VelocityChart, shortDay } from './charts'

const RANGES = [14, 30, 90] as const

/**
 * Project reports: sprint burndown, velocity, created vs resolved, and a
 * breakdown of the open work. Everything is computed on the server from the
 * board's own history; this page only chooses the sprint and the window.
 */
export default function ReportsPage() {
  const [params, setParams] = useSearchParams()
  const { slug, organization } = useWorkspace()
  const { projectKey, project, isLoading: projectsLoading } = useProjectSelection(organization?.id ?? null)

  const rangeParam = Number(params.get('days'))
  const days = (RANGES as readonly number[]).includes(rangeParam) ? rangeParam : 30
  const sprintId = params.get('sprint')

  const reports = useProjectReports(slug, projectKey, days, sprintId)

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value === null || value === '') next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }

  const upgrade = reports.error instanceof ApiError && reports.error.isUpgradeRequired

  return (
    <div className="flex h-dvh min-h-0 flex-col">
      <header className="shrink-0 border-b border-ink-200 px-4 py-3 dark:border-ink-800">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-sm font-semibold text-ink-900 dark:text-ink-100">
            {project?.name ? `${project.name} reports` : 'Reports'}
          </h1>
          {reports.data ? (
            <span className="ml-auto flex items-center gap-2">
              {reports.data.sprints.length > 0 ? (
                <Select
                  aria-label="Sprint"
                  value={sprintId ?? reports.data.burndown?.sprint_id ?? ''}
                  onChange={(event) => setParam('sprint', event.target.value)}
                  className="w-48 py-1 text-xs"
                >
                  {reports.data.sprints.map((sprint) => (
                    <option key={sprint.id} value={sprint.id}>
                      {sprint.name}
                      {sprint.status === 'active' ? ' (active)' : sprint.status === 'planning' ? ' (planned)' : ''}
                    </option>
                  ))}
                </Select>
              ) : null}
              <Select
                aria-label="Time range"
                value={String(days)}
                onChange={(event) => setParam('days', event.target.value)}
                className="w-36 py-1 text-xs"
              >
                {RANGES.map((range) => (
                  <option key={range} value={range}>
                    Last {range} days
                  </option>
                ))}
              </Select>
            </span>
          ) : null}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {projectsLoading || (reports.isLoading && projectKey) ? (
          <LoadingPane label="Loading reports" />
        ) : !projectKey ? (
          <EmptyState
            title="No project yet"
            description="Reports describe one project's work. Create a project and they fill in."
            action={<NewProjectButton variant="primary" />}
          />
        ) : upgrade ? (
          <UpgradePrompt slug={slug} />
        ) : reports.error ? (
          <ErrorBanner error={reports.error} onRetry={() => void reports.refetch()} />
        ) : reports.data ? (
          <ReportsBody data={reports.data} days={days} />
        ) : null}
      </div>
    </div>
  )
}

function UpgradePrompt({ slug }: { slug: string | null }) {
  return (
    <div className="mx-auto max-w-md py-12 text-center">
      <BarChart3 className="mx-auto size-8 text-brand-600 dark:text-brand-400" aria-hidden="true" />
      <p className="mt-4 text-sm font-medium">Reports are on the Standard and Business plans</p>
      <p className={cn('mt-1 text-sm', mutedTextClass)}>
        See sprint burndown, velocity, created vs resolved, and where your open work sits.
      </p>
      {slug ? (
        <Link
          to={`/${slug}/settings/billing`}
          className="mt-5 inline-flex items-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          See plans
        </Link>
      ) : null}
    </div>
  )
}

function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="rounded-card border border-ink-200 px-4 py-3 dark:border-ink-800">
      <p className={cn('text-xs', mutedTextClass)}>{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums tracking-tight">{value}</p>
      {hint ? <p className={cn('mt-0.5 text-xs', mutedTextClass)}>{hint}</p> : null}
    </div>
  )
}

function ReportsBody({ data, days }: { data: ProjectReports; days: number }) {
  const { burndown, velocity, flow, breakdown } = data
  const velocityByPoints = velocity.some((s) => s.completed_points > 0)
  const averageVelocity = velocity.length
    ? velocity.reduce((sum, s) => sum + (velocityByPoints ? s.completed_points : s.completed_count), 0) /
      velocity.length
    : null
  const burndownDone = burndown ? burndown.total - burndown.remaining : 0

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Open issues" value={breakdown.open} hint={`${breakdown.done} done in total`} />
        <Stat
          label={`Created · last ${days} days`}
          value={flow.created_total}
          hint={`${flow.resolved_total} resolved`}
        />
        <Stat
          label="Sprint progress"
          value={
            burndown && burndown.total > 0 ? `${Math.round((burndownDone / burndown.total) * 100)}%` : '—'
          }
          hint={burndown ? `${fmt(burndownDone)} of ${fmt(burndown.total)} ${burndown.unit}` : 'No sprint yet'}
        />
        <Stat
          label="Average velocity"
          value={averageVelocity === null ? '—' : fmt(averageVelocity)}
          hint={
            velocity.length
              ? `${velocityByPoints ? 'points' : 'issues'} per sprint, last ${velocity.length}`
              : 'No completed sprints yet'
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Sprint burndown"
            description={
              burndown
                ? `${burndown.name} · ${shortDay(burndown.start_date)} – ${shortDay(burndown.end_date)}`
                : 'Work remaining in the sprint, day by day.'
            }
            action={
              burndown ? (
                <Badge tone={burndown.status === 'active' ? 'brand' : 'neutral'}>
                  {burndown.status === 'active' ? 'Active' : burndown.status === 'completed' ? 'Completed' : 'Planned'}
                </Badge>
              ) : null
            }
          />
          <div className="space-y-3 px-5 py-4">
            {burndown && burndown.days.length > 0 && burndown.total > 0 ? (
              <>
                <BurndownChart days={burndown.days} unit={burndown.unit} />
                <Legend
                  items={[
                    { label: `Remaining ${burndown.unit}`, className: 'bg-brand-600 dark:bg-brand-400' },
                    { label: 'Ideal', className: 'border-ink-300 dark:border-ink-600', dashed: true },
                  ]}
                />
              </>
            ) : (
              <EmptyState
                className="py-8"
                title={burndown ? 'This sprint has no issues' : 'No sprint to chart yet'}
                description="Start a sprint from the backlog and its burndown appears here."
              />
            )}
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Velocity"
            description={`${velocityByPoints ? 'Story points' : 'Issues'} completed per sprint.`}
          />
          <div className="space-y-3 px-5 py-4">
            {velocity.length > 0 ? (
              <>
                <VelocityChart sprints={velocity} byPoints={velocityByPoints} />
                <Legend
                  items={[
                    { label: 'Completed', className: 'bg-brand-600 dark:bg-brand-400' },
                    ...(velocityByPoints && velocity.some((s) => s.committed_points > 0)
                      ? [{ label: 'Committed', className: 'h-2 bg-ink-200 dark:bg-ink-800' }]
                      : []),
                    ...(velocity.length > 1 ? [{ label: 'Average', className: 'border-emerald-500', dashed: true }] : []),
                  ]}
                />
              </>
            ) : (
              <EmptyState
                className="py-8"
                title="No completed sprints yet"
                description="Complete a sprint and this shows how much each one delivered."
              />
            )}
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Created vs resolved"
          description={`Issues opened and closed each day over the last ${days} days.`}
          action={
            <span className={cn('text-xs tabular-nums', mutedTextClass)}>
              {flow.created_total} created · {flow.resolved_total} resolved
            </span>
          }
        />
        <div className="space-y-3 px-5 py-4">
          <FlowChart series={flow.series} />
          <Legend
            items={[
              { label: 'Created', className: 'h-2 bg-brand-600 dark:bg-brand-400' },
              { label: 'Resolved', className: 'h-2 bg-emerald-500' },
            ]}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Open work" description={`${breakdown.open} open issues in this project.`} />
        <div className="grid gap-6 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <BreakdownColumn title="By status">
            <BucketBars buckets={breakdown.by_status} empty="Nothing open." />
          </BreakdownColumn>
          <BreakdownColumn title="By assignee">
            <BucketBars buckets={breakdown.by_assignee} empty="Nothing open." />
          </BreakdownColumn>
          <BreakdownColumn title="By priority">
            <BucketBars buckets={breakdown.by_priority} empty="Nothing open." />
          </BreakdownColumn>
          <BreakdownColumn title="By type">
            <BucketBars buckets={breakdown.by_type} empty="Nothing open." />
          </BreakdownColumn>
        </div>
      </Card>
    </div>
  )
}

function BreakdownColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className={cn('mb-3 text-xs font-medium uppercase tracking-wide', mutedTextClass)}>{title}</p>
      {children}
    </div>
  )
}

function fmt(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}
